import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, RefreshCw, Paperclip, Link as LinkIcon, X, Inbox, Settings2 } from "lucide-react";
import { toast } from "sonner";

export default function EmailInbox() {
  const [messages, setMessages] = useState([]);
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState("alle"); // alle | matched | unmatched
  const [selected, setSelected] = useState(null);
  const [configured, setConfigured] = useState(true);

  const load = async () => {
    try {
      const [msgs, anms, settings] = await Promise.all([
        api.get("/email/messages").then((x) => x.data),
        api.get("/anmeldungen").then((x) => x.data),
        api.get("/email/settings").then((x) => x.data),
      ]);
      setMessages(msgs);
      setAnmeldungen(anms);
      setConfigured(settings.configured);
    } catch (err) {
      // ignore
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return messages.filter((m) => {
      if (filter === "matched") return !!m.matched_anmeldung_id;
      if (filter === "unmatched") return !m.matched_anmeldung_id;
      return true;
    });
  }, [messages, filter]);

  const fetchNow = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/email/fetch");
      toast.success(`${data.new_count} neue Nachricht(en) · ${data.matched_count} automatisch zugeordnet`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Abruf fehlgeschlagen");
    } finally {
      setFetching(false);
    }
  };

  const assign = async (msg, anmeldungId) => {
    try {
      await api.post(`/email/messages/${msg.id}/assign`, { anmeldung_id: anmeldungId });
      toast.success("Zugeordnet");
      load();
      if (selected?.id === msg.id) {
        setSelected({ ...msg, matched_anmeldung_id: anmeldungId, auto_matched: false });
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markSeen = async (msg) => {
    if (msg.seen) return;
    try { await api.post(`/email/messages/${msg.id}/seen`); load(); } catch {}
  };

  const dismiss = async (msg) => {
    try { await api.post(`/email/messages/${msg.id}/dismiss`); toast.success("Nachricht ausgeblendet"); setSelected(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!configured) {
    return (
      <div className="p-8 space-y-6 animate-fade-up" data-testid="inbox-not-configured">
        <div>
          <div className="overline">Posteingang</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Kein Postfach verbunden</h1>
          <p className="text-muted-foreground mt-1">Verbinde zuerst dein Gmail-Postfach, um Nachrichten zu empfangen.</p>
        </div>
        <Link to="/email-einstellungen">
          <Button data-testid="setup-email-button"><Settings2 className="h-4 w-4 mr-2" /> Postfach einrichten</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6 animate-fade-up" data-testid="inbox-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2"><Inbox className="h-3 w-3" /> Posteingang</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">E-Mail-Nachrichten</h1>
          <p className="text-muted-foreground mt-1">Netzbetreiber-Mails automatisch dem passenden Kunden zugeordnet.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="inbox-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle</SelectItem>
              <SelectItem value="matched">Zugeordnet</SelectItem>
              <SelectItem value="unmatched">Nicht zugeordnet</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchNow} disabled={fetching} data-testid="fetch-now-button">
            <RefreshCw className={`h-4 w-4 mr-2 ${fetching ? "animate-spin" : ""}`} /> {fetching ? "Rufe ab …" : "Jetzt abrufen"}
          </Button>
          <Link to="/email-einstellungen">
            <Button variant="outline" size="icon" data-testid="settings-shortcut"><Settings2 className="h-4 w-4" /></Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 min-h-[60vh]">
        <Card className="border-border">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="inbox-empty">
                Keine Nachrichten in dieser Ansicht.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => { setSelected(m); markSeen(m); }}
                      className={`w-full text-left px-4 py-3 hover:bg-secondary transition-colors ${selected?.id === m.id ? "bg-secondary" : ""} ${!m.seen ? "font-medium" : ""}`}
                      data-testid={`msg-${m.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm truncate">{m.from_addr || "(unbekannter Absender)"}</div>
                          <div className="text-sm mt-0.5 truncate">{m.subject || "(kein Betreff)"}</div>
                          <div className="text-xs text-muted-foreground truncate mt-1">{m.body_snippet}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {m.matched_kunde_name && (
                            <Badge variant="outline" className={`text-[10px] ${m.auto_matched ? "border-primary text-primary" : ""}`} data-testid={`matched-${m.id}`}>
                              <LinkIcon className="h-3 w-3 mr-1" /> {m.matched_kunde_name}
                            </Badge>
                          )}
                          {!m.matched_anmeldung_id && (
                            <Badge variant="secondary" className="text-[10px]">Nicht zugeordnet</Badge>
                          )}
                          {m.attachments?.length > 0 && (
                            <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Paperclip className="h-3 w-3" /> {m.attachments.length}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="msg-detail">
          <CardContent className="p-6">
            {!selected ? (
              <div className="text-center text-sm text-muted-foreground py-20">
                <Mail className="h-10 w-10 mx-auto text-muted-foreground/50" />
                <div className="mt-3">Nachricht auswählen, um Details zu sehen</div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Von</div>
                  <div className="text-sm mt-0.5">{selected.from_addr}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Betreff</div>
                  <div className="text-sm font-medium mt-0.5">{selected.subject}</div>
                </div>

                <div className="border-t border-border pt-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Zuordnung zu Anmeldung</div>
                  <div className="flex items-center gap-2">
                    <Select value={selected.matched_anmeldung_id || ""} onValueChange={(v) => assign(selected, v)}>
                      <SelectTrigger className="w-full" data-testid="assign-select">
                        <SelectValue placeholder="Anmeldung wählen …" />
                      </SelectTrigger>
                      <SelectContent>
                        {anmeldungen.map((a) => (
                          <SelectItem key={a.id} value={a.id} data-testid={`assign-${a.id}`}>
                            {a.kunde_name} — {a.kunde_adresse}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selected.auto_matched && (
                      <Badge variant="outline" className="text-primary border-primary text-[10px] shrink-0">Auto</Badge>
                    )}
                  </div>
                </div>

                {selected.attachments?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Anhänge</div>
                    <ul className="space-y-1">
                      {selected.attachments.map((a, i) => (
                        <li key={i}>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${a.path}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                            data-testid={`attachment-${i}`}
                          >
                            <Paperclip className="h-3.5 w-3.5" /> {a.name}
                            <span className="text-xs text-muted-foreground">({Math.round((a.size || 0) / 1024)} KB)</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t border-border pt-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Inhalt</div>
                  <div className="text-sm whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                    {selected.body_snippet}
                  </div>
                </div>

                <div className="flex justify-end border-t border-border pt-3">
                  <Button variant="ghost" size="sm" onClick={() => dismiss(selected)} data-testid="dismiss-msg">
                    <X className="h-4 w-4 mr-1.5" /> Ausblenden
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
